import {
  Alert,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';
import { useInvalidate } from './CatalogPages';
import { OutletPicker } from './OutletConfigPages';

interface Station {
  id: string;
  name: string;
}
interface Area {
  id: string;
  name: string;
}
interface Table {
  id: string;
  name: string;
  areaId: string | null;
  capacity: number;
}

/** Meja & Dapur: stasiun (Dapur/Bar), area, dan meja per outlet. */
export function FnbSetupPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('settings:write');
  const [outletId, setOutletId] = useState('');
  const base = `/outlets/${outletId}`;
  const stations = useQuery({
    queryKey: ['fnb-stations', outletId],
    enabled: !!outletId,
    queryFn: () => api.get<Station[]>(`${base}/stations`),
  });
  const areas = useQuery({
    queryKey: ['fnb-areas', outletId],
    enabled: !!outletId,
    queryFn: () => api.get<Area[]>(`${base}/areas`),
  });
  const tables = useQuery({
    queryKey: ['fnb-tables', outletId],
    enabled: !!outletId,
    queryFn: () => api.get<Table[]>(`${base}/tables`),
  });
  const refresh = () => inv('fnb-stations', 'fnb-areas', 'fnb-tables', 'stations-all');

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'station' | 'area' | null>(null);
  const addSimple = useSubmit(async () => {
    await api.post(`${base}/${kind === 'station' ? 'stations' : 'areas'}`, { name: name.trim() });
    refresh();
    setKind(null);
    setName('');
  });
  const del = useSubmit(async (path: string, label: string) => {
    if (!confirm(`Hapus ${label}?`)) return;
    await api.del(path);
    refresh();
  });

  const [bulk, setBulk] = useState<{
    areaId: string;
    prefix: string;
    from: string;
    count: string;
    capacity: string;
  } | null>(null);
  const addBulk = useSubmit(async () => {
    await api.post(`${base}/tables/bulk`, {
      areaId: bulk!.areaId || null,
      prefix: bulk!.prefix,
      from: Number(bulk!.from),
      count: Number(bulk!.count),
      capacity: Number(bulk!.capacity),
    });
    refresh();
    setBulk(null);
  });
  const [edit, setEdit] = useState<(Table & { capacityText: string }) | null>(null);
  const saveTable = useSubmit(async () => {
    await api.patch(`/tables/${edit!.id}`, {
      name: edit!.name.trim(),
      areaId: edit!.areaId || null,
      capacity: Number(edit!.capacityText),
    });
    refresh();
    setEdit(null);
  });

  const areaName = (id: string | null) => areas.data?.find((a) => a.id === id)?.name ?? '—';
  const tableCols: Column<Table>[] = [
    { header: 'Meja', cell: (t) => <span className="font-medium">{t.name}</span> },
    { header: 'Area', cell: (t) => areaName(t.areaId) },
    { header: 'Kursi', cell: (t) => t.capacity },
    {
      header: '',
      className: 'text-right',
      cell: (t) =>
        write && (
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setEdit({ ...t, capacityText: String(t.capacity) })}
            >
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void del.run(`/tables/${t.id}`, t.name)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  const simpleCols = (path: string): Column<Station | Area>[] => [
    { header: 'Nama', cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      header: '',
      className: 'text-right',
      cell: (r) =>
        write && (
          <Button variant="ghost" onClick={() => void del.run(`/${path}/${r.id}`, r.name)}>
            Hapus
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Meja & Dapur"
        subtitle="Stasiun dapur/bar tujuan tiket, area, dan denah meja untuk kasir F&B"
      />
      {(del.error || addSimple.error) && <Alert>{del.error ?? addSimple.error}</Alert>}
      <div className="mb-4">
        <OutletPicker value={outletId} onChange={setOutletId} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Stasiun (Dapur, Bar)"
          actions={write && <Button onClick={() => setKind('station')}>+ Stasiun</Button>}
        >
          <p className="mb-2 text-xs text-slate-500">
            Atur tujuan tiap kategori/produk di menu Kategori & Produk. Tanpa pengaturan, tiket
            masuk ke stasiun pertama. Pemetaan antar outlet memakai nama stasiun yang sama.
          </p>
          <DataTable columns={simpleCols('stations')} rows={stations.data ?? []} />
        </Card>
        <Card
          title="Area"
          actions={write && <Button onClick={() => setKind('area')}>+ Area</Button>}
        >
          <DataTable columns={simpleCols('areas')} rows={areas.data ?? []} />
        </Card>
      </div>
      <div className="mt-4">
        <Card
          title="Meja"
          actions={
            write && (
              <Button
                onClick={() =>
                  setBulk({
                    areaId: areas.data?.[0]?.id ?? '',
                    prefix: 'Meja',
                    from: '1',
                    count: '10',
                    capacity: '4',
                  })
                }
              >
                + Tambah meja
              </Button>
            )
          }
        >
          <DataTable columns={tableCols} rows={tables.data ?? []} />
        </Card>
      </div>

      <Modal
        open={!!kind}
        title={kind === 'station' ? 'Tambah stasiun' : 'Tambah area'}
        onClose={() => setKind(null)}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void addSimple.run();
          }}
        >
          <TextField
            label="Nama"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            maxLength={40}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setKind(null)}>
              Batal
            </Button>
            <Button type="submit" disabled={addSimple.busy || !name.trim()}>
              Simpan
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!bulk} title="Tambah meja" onClose={() => setBulk(null)}>
        {bulk && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void addBulk.run();
            }}
          >
            {addBulk.error && <Alert>{addBulk.error}</Alert>}
            <p className="text-xs text-slate-500">
              Contoh: awalan “Meja”, mulai 1, jumlah 10 → Meja 1 … Meja 10.
            </p>
            <SelectField
              label="Area"
              value={bulk.areaId}
              onChange={(e) => setBulk({ ...bulk, areaId: e.target.value })}
            >
              <option value="">— Tanpa area —</option>
              {areas.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </SelectField>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Awalan"
                value={bulk.prefix}
                onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })}
              />
              <TextField
                label="Mulai dari"
                inputMode="numeric"
                value={bulk.from}
                onChange={(e) => setBulk({ ...bulk, from: e.target.value.replace(/\D/g, '') })}
              />
              <TextField
                label="Jumlah meja"
                inputMode="numeric"
                value={bulk.count}
                onChange={(e) => setBulk({ ...bulk, count: e.target.value.replace(/\D/g, '') })}
              />
              <TextField
                label="Kursi per meja"
                inputMode="numeric"
                value={bulk.capacity}
                onChange={(e) => setBulk({ ...bulk, capacity: e.target.value.replace(/\D/g, '') })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setBulk(null)}>
                Batal
              </Button>
              <Button
                type="submit"
                disabled={
                  addBulk.busy ||
                  !Number(bulk.count) ||
                  !Number(bulk.from) ||
                  !Number(bulk.capacity)
                }
              >
                Buat meja
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!edit} title="Ubah meja" onClose={() => setEdit(null)}>
        {edit && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void saveTable.run();
            }}
          >
            {saveTable.error && <Alert>{saveTable.error}</Alert>}
            <TextField
              label="Nama meja"
              value={edit.name}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              required
              maxLength={20}
            />
            <SelectField
              label="Area"
              value={edit.areaId ?? ''}
              onChange={(e) => setEdit({ ...edit, areaId: e.target.value || null })}
            >
              <option value="">— Tanpa area —</option>
              {areas.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Kursi"
              inputMode="numeric"
              value={edit.capacityText}
              onChange={(e) =>
                setEdit({ ...edit, capacityText: e.target.value.replace(/\D/g, '') })
              }
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEdit(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={saveTable.busy || !Number(edit.capacityText)}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
